'use client';

import { styled } from '@linaria/react';
import PopTopicCardContent from './PopTopicCardContent';
import PopTopicCardTitle from './PopTopicCardTitle';
import PopTopicGroupTags from './PopTopicGroupTags';
import Image from 'next/image';
import { PopTopicListData } from '@fe-news/constants/pop-topic/type';
import { useImperativeHandle, useRef, forwardRef } from 'react';
import useFetchInfinitePopTopic from '@fe-news/hooks/fetchInfiniteList/use-fetch-infinite-pop-topic';

const PopTopicCardWrapper = styled.div<{ isCarousel: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  border: 1px solid #e2e8f1;
  background-color: ${({ isCarousel }) => (isCarousel ? '#f6f8fc' : '#fff')};
  box-shadow: ${({ isCarousel }) => (isCarousel ? '0 0 6px 0 rgba(0, 47, 143, 0.1)' : 'none')};
  position: relative;
  min-height: ${({ isCarousel }) => (isCarousel ? '640px' : 'auto')};

  &:hover {
    box-shadow: 0 0 6px 0 rgba(0, 65, 143, 0.1);
  }
`;

const Ribbons = styled(Image)`
  position: absolute;
  z-index: 2;
  top: -7px;
  left: -8px;
`;

type PopTopicCardProps = {
  data: PopTopicListData;
  isCarousel?: boolean;
  ref?: React.RefObject<HTMLElement>;
};

// eslint-disable-next-line react/display-name
const PopTopicCard = forwardRef<HTMLDivElement, PopTopicCardProps>(
  // eslint-disable-next-line unicorn/prevent-abbreviations
  ({ data, isCarousel = false }, ref) => {
    const localReference = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line unicorn/prevent-abbreviations
    useImperativeHandle(ref, () => localReference.current as HTMLDivElement);

    const { data: relativeNews, isError } = useFetchInfinitePopTopic({
      id: data.id,
      isDetailPage: false
    });

    const tags = new Set([...data.tagOr, ...data.tagAnd].filter(tag => tag !== ''));

    const defaultImageId = relativeNews?.[0]?.newsId ?? 1;

    const defaultImage = `/assets/images/news_default_images/${defaultImageId % 10}.webp`;

    const bgImage = relativeNews?.[0]?.coverSrc?.l.src ?? defaultImage;

    if (isError) {
      return <div>isError:{data.id}</div>;
    }

    return (
      <PopTopicCardWrapper
        ref={localReference}
        isCarousel={isCarousel}
        style={{ margin: isCarousel ? '0px 8px' : '0px' }}
      >
        {isCarousel && (
          <Ribbons src='/assets/icons/label-ribbons.png' height={84} width={77} alt='ribbons' unoptimized />
        )}
        {!isCarousel && data.categories && <PopTopicGroupTags hotIssueTag={data.categories} />}
        <PopTopicCardTitle
          title={data.title}
          summary={data.content}
          tags={[...tags]}
          id={data.id}
          isCarousel={isCarousel}
          bgImage={bgImage}
        />
        <PopTopicCardContent data={relativeNews} id={data.id} isCarousel={isCarousel} title={data.title} />
      </PopTopicCardWrapper>
    );
  }
);

export default PopTopicCard;
